import { logger } from "@/utils/logger";
import { cities } from "../configs";

export interface GeocodeResult {
	formattedAddress: string;
	locality: string;
	city: string;
	postalCode: string;
	projectName: string;
}

export const geocodeLocation = (position: { lat: number; lng: number }): Promise<GeocodeResult> => {
	return new Promise((resolve, reject) => {
		try {
			const geocoder = new google.maps.Geocoder();
			geocoder.geocode({ location: position }, (results, status) => {
				if (status === "OK" && results?.[0]) {
					const addressComponents = results[0]?.address_components;
					const formattedAddress = results[0]?.formatted_address;
					let locality = "";
					let city = "Bengaluru";
					let postalCode = "";
					let projectName = "";

					// Extract project name from the first part of formatted address or place name
					// if (results[0].formatted_address) {
					// 	const addressParts = results[0].formatted_address.split(",");
					// 	// Use the first part only if it's not too generic
					// 	const firstPart = addressParts[0]?.trim() || "";
					// 	if (firstPart && !firstPart.match(/^\d+$/)) {
					// 		// Not just a number
					// 		projectName = firstPart;
					// 	}
					// }

					(addressComponents || []).forEach(component => {
						if (!component.long_name) return;

						const types = component.types;
						// Try premise as backup for project name
						if (types.includes("premise")) {
							projectName = component.long_name;
						}
						// Handle different location type priorities
						if (types.includes("postal_code")) {
							postalCode = component.long_name;
						}
						if (types.includes("sublocality_level_1") || types.includes("sublocality")) {
							locality = component.long_name;
						}
						if (types.includes("sublocality_level_2") && !locality) {
							locality = component.long_name;
						}
						if (types.includes("locality")) {
							city = component.long_name;
						}
						// Also check for establishment or point_of_interest for project names
						if (types.includes("establishment") || (types.includes("point_of_interest") && !projectName)) {
							projectName = component.long_name;
						}
					});

					// If no postal code found, try short_name
					if (!postalCode) {
						addressComponents.forEach(component => {
							if (component.types.includes("postal_code") && component.short_name) {
								postalCode = component.short_name;
							}
						});
					}

					if (city) city = checkLocation.findCityIdByName(city) || "bengaluru";

					resolve({
						formattedAddress,
						locality,
						city: city || "bengaluru",
						postalCode,
						projectName,
					});
				} else {
					logger.error("Geocode failed with status:", status);
					reject(new Error(`Geocoding failed: ${status}`));
				}
			});
		} catch (error) {
			logger.error("Error in geocoding:", error);
			reject(error);
		}
	});
};

export const checkLocation = {
	/**
	 * Validates that coordinates are finite numbers within valid ranges.
	 * @param lat - Latitude to validate
	 * @param lng - Longitude to validate
	 * @returns true if coordinates are valid, false otherwise
	 */
	validateCoordinates: (lat: number, lng: number): boolean => {
		// Check for finite numbers (not NaN, Infinity, or -Infinity)
		if (!isFinite(lat) || !isFinite(lng)) {
			return false;
		}

		// Check for valid coordinate ranges
		if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
			return false;
		}

		return true;
	},

	/**
	 * Finds the city object if the coordinates fall within any defined city bounds.
	 * Returns the city object or undefined if no match is found.
	 */
	findCityByCoords: (lat: number, lng: number) => {
		// Validate coordinates first
		if (!checkLocation.validateCoordinates(lat, lng)) {
			return undefined;
		}

		return cities.find(city => {
			const { north, south, east, west } = city.bounds;
			return lat <= north && lat >= south && lng <= east && lng >= west;
		});
	},

	findCityIdByName: (name: string) => {
		// Sanitize input
		if (typeof name !== "string") {
			return undefined;
		}

		name = name.toLowerCase().trim();

		// Prevent empty strings from matching
		if (!name) {
			return undefined;
		}

		return cities.find(({ _id, names }) => {
			return _id === name || names.map(el => el.toLowerCase().trim()).includes(name);
		})?._id;
	},

	/**
	 * Simple boolean check to see if coordinates are "serviceable"
	 * (exists in any of our city bounds)
	 */
	isLocationSupported: (lat: number, lng: number): boolean => {
		// Validate coordinates first
		if (!checkLocation.validateCoordinates(lat, lng)) {
			return false;
		}

		return cities.some(city => {
			const { north, south, east, west } = city.bounds;
			return lat <= north && lat >= south && lng <= east && lng >= west;
		});
	},
};
